import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, ArrowRight, Clock, GitBranch, Shield,
  Users, FileText, Settings, BarChart2, GraduationCap,
  Layers, Package, Zap, ChevronRight
} from 'lucide-react';
import { getAllStages } from '../../lib/leadPipelineRegistry';
import { getAllRoles } from '../../lib/rolesRegistry';
import { getEscalationRules, getExceptionCodes, getStageSLAs, getChannelSLAs } from '../../lib/slaCatalogueRegistry';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SearchResult {
  id: string;
  title: string;
  titleAr?: string;
  subtitle: string;
  subtitleAr?: string;
  category: string;
  categoryAr: string;
  path: string;
  icon: React.ReactNode;
  tags: string[];
  score: number;
}

interface SearchCategory {
  id: string;
  label: string;
  labelAr: string;
  icon: React.ReactNode;
  color: string;
}

// ─── Category Definitions ─────────────────────────────────────────────────────
const CATEGORIES: SearchCategory[] = [
  { id: 'stage', label: 'Pipeline Stages', labelAr: 'مراحل المسار', icon: <GitBranch className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
  { id: 'role', label: 'Roles', labelAr: 'الأدوار', icon: <Users className="w-4 h-4" />, color: 'text-emerald-600 bg-emerald-50' },
  { id: 'sla', label: 'SLA Rules', labelAr: 'قواعد SLA', icon: <Clock className="w-4 h-4" />, color: 'text-orange-600 bg-orange-50' },
  { id: 'escalation', label: 'Escalation Rules', labelAr: 'قواعد التصعيد', icon: <Shield className="w-4 h-4" />, color: 'text-red-600 bg-red-50' },
  { id: 'exception', label: 'Exception Codes', labelAr: 'رموز الاستثناء', icon: <Zap className="w-4 h-4" />, color: 'text-purple-600 bg-purple-50' },
  { id: 'page', label: 'Pages & Modules', labelAr: 'الصفحات والوحدات', icon: <Layers className="w-4 h-4" />, color: 'text-slate-600 bg-slate-50' }
];

// ─── Static page index ────────────────────────────────────────────────────────
const STATIC_PAGES: Array<Omit<SearchResult, 'score'>> = [
  { id: 'p-dashboard', title: 'Executive Dashboard', titleAr: 'لوحة التحكم التنفيذية', subtitle: 'Live KPIs, brand filter, pipeline overview', subtitleAr: 'مؤشرات الأداء الحية وفلتر العلامة التجارية ونظرة عامة على المسار', category: 'page', categoryAr: 'صفحة', path: '/dashboard', icon: <BarChart2 className="w-4 h-4" />, tags: ['dashboard', 'kpi', 'overview', 'brand'] },
  { id: 'p-pipeline', title: 'Lead Pipeline', titleAr: 'مسار العملاء المحتملين', subtitle: 'Interactive 11-stage pipeline with SLA and RACI', subtitleAr: 'مسار تفاعلي من 11 مرحلة مع SLA و RACI', category: 'page', categoryAr: 'صفحة', path: '/pipeline', icon: <GitBranch className="w-4 h-4" />, tags: ['pipeline', 'stages', 'flow', 'sla'] },
  { id: 'p-performance', title: 'Performance Management', titleAr: 'إدارة الأداء', subtitle: 'KPI overview, framework, role scorecards', subtitleAr: 'نظرة عامة KPI والإطار وبطاقات أداء الأدوار', category: 'page', categoryAr: 'صفحة', path: '/performance', icon: <BarChart2 className="w-4 h-4" />, tags: ['performance', 'kpi', 'scorecard', 'target'] },
  { id: 'p-policies', title: 'Policies & Governance', titleAr: 'السياسات والحوكمة', subtitle: 'Data quality, escalation, exception, brand policies', subtitleAr: 'سياسات جودة البيانات والتصعيد والاستثناءات', category: 'page', categoryAr: 'صفحة', path: '/policies', icon: <Shield className="w-4 h-4" />, tags: ['policies', 'governance', 'compliance', 'escalation'] },
  { id: 'p-crm', title: 'CRM Configuration', titleAr: 'تكوين CRM', subtitle: 'Stage mapping, automations, field config, integrations', subtitleAr: 'تخطيط المراحل والأتمتة وتكوين الحقول والتكاملات', category: 'page', categoryAr: 'صفحة', path: '/crm', icon: <Settings className="w-4 h-4" />, tags: ['crm', 'automation', 'fields', 'integration', 'configuration'] },
  { id: 'p-implementation', title: 'Implementation Guide', titleAr: 'دليل التنفيذ', subtitle: '6-phase rollout plan with progress tracker', subtitleAr: 'خطة طرح من 6 مراحل مع متتبع التقدم', category: 'page', categoryAr: 'صفحة', path: '/implementation', icon: <Package className="w-4 h-4" />, tags: ['implementation', 'rollout', 'phases', 'tracker'] },
  { id: 'p-training', title: 'Training & Resources', titleAr: 'التدريب والموارد', subtitle: '16 modules, 10 role paths, 26 certifications', subtitleAr: '16 وحدة، 10 مسارات، 26 شهادة', category: 'page', categoryAr: 'صفحة', path: '/training', icon: <GraduationCap className="w-4 h-4" />, tags: ['training', 'modules', 'certification', 'learning'] },
  { id: 'p-tools', title: 'Tools & Templates', titleAr: 'الأدوات والقوالب', subtitle: 'WhatsApp/SMS templates, call scripts, checklists', subtitleAr: 'قوالب واتساب/SMS ونصوص المكالمات وقوائم المراجعة', category: 'page', categoryAr: 'صفحة', path: '/tools', icon: <Package className="w-4 h-4" />, tags: ['tools', 'templates', 'whatsapp', 'scripts', 'checklist'] },
  { id: 'p-roles', title: 'Roles & Responsibilities', titleAr: 'الأدوار والمسؤوليات', subtitle: '10 role profiles, RACI matrix, coverage model', subtitleAr: '10 ملفات أدوار ومصفوفة RACI ونموذج التغطية', category: 'page', categoryAr: 'صفحة', path: '/roles', icon: <Users className="w-4 h-4" />, tags: ['roles', 'responsibilities', 'raci', 'team'] },
  { id: 'p-sops', title: 'Standard Operating Procedures', titleAr: 'إجراءات التشغيل الموحدة', subtitle: '16 SOPs across all pipeline stages and scenarios', subtitleAr: '16 إجراء عبر جميع مراحل المسار والسيناريوهات', category: 'page', categoryAr: 'صفحة', path: '/sops', icon: <FileText className="w-4 h-4" />, tags: ['sops', 'procedures', 'standards', 'operations'] },
  { id: 'p-sla', title: 'SLA Catalogue', titleAr: 'كتالوج SLA', subtitle: '8 channel SLAs, 11 stage SLAs, 6 escalation rules', subtitleAr: '8 اتفاقيات قنوات، 11 اتفاقية مراحل، 6 قواعد تصعيد', category: 'page', categoryAr: 'صفحة', path: '/sla-catalogue', icon: <Clock className="w-4 h-4" />, tags: ['sla', 'response', 'time', 'breach', 'target'] },
  { id: 'p-operating', title: 'Operating Model', titleAr: 'نموذج التشغيل', subtitle: 'Org structure, daily cadences, governance forums', subtitleAr: 'الهيكل التنظيمي والجداول اليومية ومنتديات الحوكمة', category: 'page', categoryAr: 'صفحة', path: '/operating-model', icon: <Settings className="w-4 h-4" />, tags: ['operating', 'model', 'org', 'cadence', 'governance', 'forum'] }
];

// ─── Score function ───────────────────────────────────────────────────────────
function scoreResult(result: Omit<SearchResult, 'score'>, query: string): number {
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  const titleMatch = result.title.toLowerCase().includes(q) ? 10 : 0;
  const titleArMatch = (result.titleAr || '').includes(q) ? 10 : 0;
  const subtitleMatch = result.subtitle.toLowerCase().includes(q) ? 5 : 0;
  const subtitleArMatch = (result.subtitleAr || '').includes(q) ? 5 : 0;
  const tagMatch = result.tags.some(t => t.toLowerCase().includes(q)) ? 3 : 0;
  const exactTitle = result.title.toLowerCase() === q ? 5 : 0;
  return titleMatch + titleArMatch + subtitleMatch + subtitleArMatch + tagMatch + exactTitle;
}

// ─── GlobalSearch Component ───────────────────────────────────────────────────
interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch = ({ isOpen, onClose }: GlobalSearchProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Build search index from all registries
  const buildIndex = useCallback((): Omit<SearchResult, 'score'>[] => {
    const index: Omit<SearchResult, 'score'>[] = [...STATIC_PAGES];

    // Pipeline stages
    try {
      const stages = getAllStages();
      stages.forEach(stage => {
        index.push({
          id: `stage-${stage.meta.id}`,
          title: stage.meta.title,
          titleAr: stage.meta.titleAr,
          subtitle: `Stage ${stage.meta.order} · Owner: ${stage.meta.owner} · SLA: ${stage.meta.sla}`,
          subtitleAr: `المرحلة ${stage.meta.order} · المالك: ${stage.meta.ownerAr} · SLA: ${stage.meta.slaAr}`,
          category: 'stage',
          categoryAr: 'مرحلة',
          path: `/pipeline/${stage.meta.id}`,
          icon: <GitBranch className="w-4 h-4" />,
          tags: ['stage', 'pipeline', stage.meta.id, stage.meta.owner.toLowerCase()]
        });
      });
    } catch { /* registry not loaded */ }

    // Roles
    try {
      const roles = getAllRoles();
      roles.forEach(role => {
        index.push({
          id: `role-${role.id}`,
          title: role.title,
          titleAr: role.titleAr,
          subtitle: `${role.department} · ${role.group} group`,
          subtitleAr: `${role.departmentAr} · مجموعة ${role.group}`,
          category: 'role',
          categoryAr: 'دور',
          path: `/roles/${role.id}`,
          icon: <Users className="w-4 h-4" />,
          tags: ['role', role.group, role.id, role.title.toLowerCase()]
        });
      });
    } catch { /* registry not loaded */ }

    // Escalation rules
    try {
      const escalations = getEscalationRules();
      escalations.forEach(rule => {
        index.push({
          id: `esc-${rule.id}`,
          title: rule.name,
          titleAr: rule.nameAr,
          subtitle: `Escalate to: ${rule.escalateTo} · ${rule.triggerType} trigger`,
          subtitleAr: `تصعيد إلى: ${rule.escalateToAr}`,
          category: 'escalation',
          categoryAr: 'تصعيد',
          path: '/policies/escalation-rules',
          icon: <Shield className="w-4 h-4" />,
          tags: ['escalation', 'rule', rule.triggerType, rule.escalateTo.toLowerCase()]
        });
      });
    } catch { /* registry not loaded */ }

    // Exception codes
    try {
      const exceptions = getExceptionCodes();
      exceptions.forEach(ex => {
        index.push({
          id: `ex-${ex.code}`,
          title: `${ex.code} – ${ex.label}`,
          titleAr: `${ex.code} – ${ex.labelAr}`,
          subtitle: ex.description,
          subtitleAr: ex.descriptionAr,
          category: 'exception',
          categoryAr: 'استثناء',
          path: '/policies/exception-codes',
          icon: <Zap className="w-4 h-4" />,
          tags: ['exception', ex.code, ex.label.toLowerCase()]
        });
      });
    } catch { /* registry not loaded */ }

    // Stage SLAs
    try {
      const stageSLAs = getStageSLAs();
      stageSLAs.forEach(sla => {
        index.push({
          id: `sla-stage-${sla.stageId}`,
          title: `${sla.stageName} SLA`,
          titleAr: `${sla.stageNameAr} – SLA`,
          subtitle: `Target: ${sla.targetDuration} · Follow-up: ${sla.followUpSLA}`,
          subtitleAr: `الهدف: ${sla.targetDurationAr}`,
          category: 'sla',
          categoryAr: 'SLA',
          path: `/sla-catalogue/${sla.stageId}`,
          icon: <Clock className="w-4 h-4" />,
          tags: ['sla', 'stage', sla.stageId, sla.stageName.toLowerCase()]
        });
      });
    } catch { /* registry not loaded */ }

    // Channel SLAs
    try {
      const channelSLAs = getChannelSLAs();
      channelSLAs.forEach(sla => {
        index.push({
          id: `sla-ch-${sla.id}`,
          title: `${sla.channel} SLA`,
          titleAr: `${sla.channelAr} – SLA`,
          subtitle: `Response: ${sla.timeToFirstContact} · Priority: ${sla.priority}`,
          subtitleAr: `وقت الاستجابة: ${sla.timeToFirstContactAr}`,
          category: 'sla',
          categoryAr: 'SLA',
          path: `/sla-catalogue/${sla.id}`,
          icon: <Clock className="w-4 h-4" />,
          tags: ['sla', 'channel', sla.id, sla.channel.toLowerCase(), sla.channelType]
        });
      });
    } catch { /* registry not loaded */ }

    return index;
  }, []);

  // Run search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    const index = buildIndex();
    const scored = index
      .map(item => ({ ...item, score: scoreResult(item, query) }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20);

    setResults(scored);
    setSelectedIndex(0);
  }, [query, buildIndex]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults([]);
      setActiveCategory('all');
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, filteredResults.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && filteredResults[selectedIndex]) {
        navigate(filteredResults[selectedIndex].path);
        onClose();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, selectedIndex, results, onClose, navigate]);

  const filteredResults = activeCategory === 'all'
    ? results
    : results.filter(r => r.category === activeCategory);

  const getCategoryDef = (catId: string) => CATEGORIES.find(c => c.id === catId);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onClose();
  };

  if (!isOpen) return null;

  // Counts per category
  const categoryCounts: Record<string, number> = {};
  results.forEach(r => {
    categoryCounts[r.category] = (categoryCounts[r.category] || 0) + 1;
  });

  const activeCats = Object.keys(categoryCounts).filter(c => categoryCounts[c] > 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-16 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Search Panel */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden z-10">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search stages, roles, SLAs, policies, tools..."
            className="flex-1 text-gray-900 placeholder-gray-400 text-base outline-none"
            autoComplete="off"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="text-xs text-gray-400 border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-50"
          >
            ESC
          </button>
        </div>

        {/* Category Filter */}
        {query && activeCats.length > 1 && (
          <div className="flex gap-2 px-4 py-2 border-b border-gray-100 overflow-x-auto">
            <button
              onClick={() => setActiveCategory('all')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                activeCategory === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({results.length})
            </button>
            {activeCats.map(cat => {
              const catDef = getCategoryDef(cat);
              if (!catDef) return null;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat
                      ? `${catDef.color} ring-1 ring-current`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {catDef.icon}
                  {catDef.label} ({categoryCounts[cat]})
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!query && (
            <div className="p-6">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Quick Navigation</div>
              <div className="grid grid-cols-2 gap-2">
                {STATIC_PAGES.slice(0, 8).map(page => (
                  <button
                    key={page.id}
                    onClick={() => handleResultClick({ ...page, score: 0 })}
                    className="flex items-center gap-2 p-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left transition-colors"
                  >
                    <span className="text-gray-400">{page.icon}</span>
                    {page.title}
                    <ChevronRight className="w-3 h-3 text-gray-300 ml-auto" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {query && filteredResults.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No results for "{query}"</p>
              <p className="text-xs mt-1">Try searching for a stage name, role, or module</p>
            </div>
          )}

          {filteredResults.length > 0 && (
            <div className="py-2">
              {filteredResults.map((result, i) => {
                const catDef = getCategoryDef(result.category);
                return (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                      i === selectedIndex ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Category Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${catDef?.color || 'bg-gray-100 text-gray-500'}`}>
                      {result.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm truncate">
                          {result.title}
                        </span>
                        {result.titleAr && (
                          <span className="text-xs text-gray-400 truncate">{result.titleAr}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{result.subtitle}</p>
                    </div>

                    {/* Category badge */}
                    <div className="flex items-center gap-1 shrink-0">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${catDef?.color || 'bg-gray-100 text-gray-500'}`}>
                        {catDef?.label || result.category}
                      </span>
                      <ArrowRight className={`w-3 h-3 ${i === selectedIndex ? 'text-indigo-500' : 'text-gray-300'}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="bg-white border border-gray-200 rounded px-1">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="bg-white border border-gray-200 rounded px-1">↵</kbd> select
            </span>
          </div>
          <div className="text-xs text-gray-400">
            {filteredResults.length > 0 ? `${filteredResults.length} results` : 'Almajdouie Lead Management'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
